import {StyleSheet, css} from 'aphrodite';
import classNames from 'classnames';
import React, {useState} from 'react';

import {assert} from 'Asserts';
import {UpdateBenchlingAppDefinitionVersionArgs} from 'benchling-apps/BenchlingAppDAO';
import {compareVersions} from 'benchling-apps/lib';
import BenchlingConfig from 'BenchlingConfig';
import {PublicationState} from 'graphql-client/__generated/graphqlGlobalTypes';
import notifications from 'notifications';
import Col from 'ui-library/bs/Col';
import {GRAY} from 'ui-library/Colors';

import * as AppDefinitionActions from '../AppDefinitionActions';
import * as BenchlingAppActions from '../BenchlingAppActions';
import {AppDefinition, AppDefinitionVersion} from './BenchlingAppsPage';
import {BenchlingAppVersionPane} from './BenchlingAppVersionPane';
import VersionHistoryItem, {
  VersionCategory,
  VersionCategoryHeader,
  VersionNewDraft,
} from './VersionHistoryItem';

// Date format used in left col (VersionsHistoryList) and right col footer (VersionPaneFooter).
export const DATE_FORMAT = 'MMMM D, YYYY';

interface BenchlingAppVersionHistoryProps {
  appDefinition: AppDefinition;
  appDefinitionVersions: Array<AppDefinitionVersion>;
  refetchAppDefinition: () => void;
}

export default function BenchlingAppVersionHistory({
  appDefinition,
  refetchAppDefinition,
  appDefinitionVersions,
}: BenchlingAppVersionHistoryProps): JSX.Element {
  const [selectedVersion, selectVersion] = useState<AppDefinitionVersion>(
    appDefinition.latestReleaseVersion!
  );

  const sortedVersions = [...appDefinitionVersions].sort(compareVersions).reverse();

  async function handleSubmitDefinitionVersion(
    newValue: UpdateBenchlingAppDefinitionVersionArgs
  ): Promise<void> {
    if (newValue.allowList && newValue.allowList.length > 0) {
      await AppDefinitionActions.shareAppDefinitionVersion(
        appDefinition.id,
        selectedVersion.versionId,
        newValue.allowList
      );
      notifications.success(
        <>
          <em>{selectedVersion.name}</em> shared successfully
        </>
      );
    } else {
      if (BenchlingConfig.ENABLE_BENCHLING_APPS_CREATE_INSTALL_SEPARATION) {
        await AppDefinitionActions.updateAppDefinitionVersion(
          appDefinition.id,
          selectedVersion.versionId,
          newValue
        );
      } else {
        assert(
          appDefinition.installation != null,
          'Expected app definition to have an associated installation'
        );
        await BenchlingAppActions.deprecatedUpdateDefinitionVersion(
          appDefinition.installation.id,
          selectedVersion.versionId,
          newValue
        );
      }

      notifications.success(<>Successfully updated app</>);
    }
    refetchAppDefinition();
  }

  return (
    <div className={classNames(css(styles.twoColumnParent))}>
      <Col md={3} className={classNames(css(styles.leftCol))}>
        <VersionsHistoryList
          appDefinition={appDefinition}
          versions={sortedVersions}
          selectedVersion={selectedVersion}
          selectVersion={selectVersion}
          onRequestNewVersion={refetchAppDefinition}
        />
      </Col>
      <Col md={9} className={classNames(css(styles.versionPane, styles.border))}>
        <BenchlingAppVersionPane
          appDefinition={appDefinition}
          version={selectedVersion}
          handleSubmitDefinitionVersion={handleSubmitDefinitionVersion}
          refetchAppDefinition={refetchAppDefinition}
        />
      </Col>
    </div>
  );
}

interface VersionHistoryListProps {
  appDefinition: AppDefinition;
  versions: Array<AppDefinitionVersion>;
  selectedVersion: AppDefinitionVersion;
  selectVersion: (version: AppDefinitionVersion) => void;
  onRequestNewVersion: () => void;
}

export function VersionsHistoryList(props: VersionHistoryListProps): JSX.Element {
  const currentlyInstalledVersion = props.appDefinition.installation?.versionNumber || null;
  const currentlyPublished = getCurrentlyPublishedVersion();

  function getCurrentlyPublishedVersion(): AppDefinitionVersion | null {
    const publishedVersions = getPublishedVersions();
    if (publishedVersions.length > 0) {
      return publishedVersions[0];
    }
    return null;
  }

  function getVersionHistory(): Array<AppDefinitionVersion> {
    const publishedVersions = getPublishedVersions();
    if (publishedVersions.length > 1) {
      return publishedVersions.slice(1);
    }
    return [];
  }

  function getDraftVersions(): Array<AppDefinitionVersion> {
    return props.versions.filter(
      (v: AppDefinitionVersion) => v.publicationState === PublicationState.PRIVATE
    );
  }

  function getPublishedVersions(): Array<AppDefinitionVersion> {
    return props.versions.filter(
      (v: AppDefinitionVersion) => v.publicationState !== PublicationState.PRIVATE
    );
  }

  return (
    <>
      <VersionNewDraft
        appDefinition={props.appDefinition}
        currentVersions={props.versions}
        onRequestSubmit={props.onRequestNewVersion}
      />

      {currentlyPublished && <VersionCategoryHeader category={VersionCategory.Published} />}
      {currentlyPublished && (
        <VersionHistoryItem
          version={currentlyPublished}
          isSelected={props.selectedVersion.versionId === currentlyPublished.versionId}
          isCurrentlyInstalled={currentlyInstalledVersion === currentlyPublished.versionId}
          selectVersion={props.selectVersion}
          key={-1}
        />
      )}
      <VersionCategoryHeader category={VersionCategory.Drafts} showZero={getDraftVersions().length === 0} />
      {getDraftVersions().map((item, i) => (
        <VersionHistoryItem
          version={item}
          isSelected={props.selectedVersion.versionId === item.versionId}
          isCurrentlyInstalled={currentlyInstalledVersion === item.versionId}
          selectVersion={props.selectVersion}
          key={i}
        />
      ))}
      {currentlyPublished && (
        <VersionCategoryHeader
          category={VersionCategory.History}
          showZero={getVersionHistory().length === 0}
        />
      )}
      {getVersionHistory().map((item, i) => (
        <VersionHistoryItem
          version={item}
          isSelected={props.selectedVersion.versionId === item.versionId}
          isCurrentlyInstalled={currentlyInstalledVersion === item.versionId}
          selectVersion={props.selectVersion}
          key={i}
        />
      ))}
    </>
  );
}

const styles = StyleSheet.create({
  twoColumnParent: {
    display: 'flex',
    position: 'relative',
  },
  versionPane: {
    flex: '0 0 75%',
    left: '25%',
  },
  leftCol: {
    position: 'absolute',
    left: 0,
    top: 0,
    bottom: 0,
    right: '75%',
    overflow: 'auto',
    padding: 0,
    border: 'solid 1px',
    borderColor: GRAY,
    marginLeft: 1,
    marginRight: 1,
  },
  border: {
    border: 'solid 1px',
    borderColor: GRAY,
  },
});
